import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

/** The authenticated editor route that Task 11 renders inside the shell. */
export type EditorRoute = { classId: string; canvasId: string };

export type Route =
  | { name: "home" }
  | { name: "login" }
  | { name: "nameSetup" }
  | { name: "school" }
  | { name: "password" }
  | { name: "account" }
  | { name: "guide" }
  | { name: "manual" }
  | { name: "studentManual" }
  | { name: "gallery" }
  | { name: "beta" }
  | { name: "unsubscribe" }
  | { name: "adminHome" }
  | { name: "adminAudit" }
  | { name: "adminReports" }
  | { name: "adminTeacherActivity" }
  | { name: "adminLive" }
  | { name: "adminTeachers" }
  | { name: "adminBetaApplications" }
  | { name: "adminGallery" }
  | { name: "classes" }
  | { name: "class"; classId: string }
  | { name: "classHandout"; classId: string }
  | { name: "canvas"; classId: string; canvasId: string }
  | { name: "revisions"; classId: string; canvasId: string }
  | { name: "notFound" };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const NOT_FOUND: Route = { name: "notFound" };

const uuid = (value: string | undefined): string | null =>
  value !== undefined && UUID.test(value) ? value.toLowerCase() : null;

const STATIC_ROUTES: Record<string, Route> = {
  login: { name: "login" },
  beta: { name: "beta" },
  unsubscribe: { name: "unsubscribe" },
  name: { name: "nameSetup" },
  school: { name: "school" },
  password: { name: "password" },
  account: { name: "account" },
  guide: { name: "guide" },
  manual: { name: "manual" },
  gallery: { name: "gallery" },
};

export const parseRoute = (pathname: string): Route => {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 0) {
    return { name: "home" };
  }
  if (parts.length === 1 && STATIC_ROUTES[parts[0]]) {
    return STATIC_ROUTES[parts[0]];
  }
  if (parts.length === 2 && parts[0] === "manual" && parts[1] === "student") {
    return { name: "studentManual" };
  }
  if (parts.length === 1 && parts[0] === "admin") {
    return { name: "adminHome" };
  }
  if (parts.length === 2 && parts[0] === "admin" && parts[1] === "audit") {
    return { name: "adminAudit" };
  }
  if (parts.length === 2 && parts[0] === "admin" && parts[1] === "reports") {
    return { name: "adminReports" };
  }
  if (
    parts.length === 2 &&
    parts[0] === "admin" &&
    parts[1] === "teacher-activity"
  ) {
    return { name: "adminTeacherActivity" };
  }
  if (parts.length === 2 && parts[0] === "admin" && parts[1] === "live") {
    return { name: "adminLive" };
  }
  if (parts.length === 2 && parts[0] === "admin" && parts[1] === "gallery") {
    return { name: "adminGallery" };
  }
  if (parts.length === 2 && parts[0] === "admin" && parts[1] === "teachers") {
    return { name: "adminTeachers" };
  }
  if (
    parts.length === 2 &&
    parts[0] === "admin" &&
    parts[1] === "beta-applications"
  ) {
    return { name: "adminBetaApplications" };
  }
  if (parts[0] !== "classes") {
    return NOT_FOUND;
  }
  if (parts.length === 1) {
    return { name: "classes" };
  }
  const classId = uuid(parts[1]);
  if (!classId) {
    return NOT_FOUND;
  }
  if (parts.length === 2) {
    return { name: "class", classId };
  }
  if (parts.length === 3 && parts[2] === "handout") {
    return { name: "classHandout", classId };
  }
  const canvasId = parts[2] === "canvases" ? uuid(parts[3]) : null;
  if (!canvasId) {
    return NOT_FOUND;
  }
  if (parts.length === 4) {
    return { name: "canvas", classId, canvasId };
  }
  if (parts.length === 5 && parts[4] === "revisions") {
    return { name: "revisions", classId, canvasId };
  }
  return NOT_FOUND;
};

export type ClassTab =
  | "canvases"
  | "students"
  | "gallery"
  | "trash"
  | "settings";

const CLASS_TABS: ReadonlySet<string> = new Set([
  "canvases",
  "students",
  "gallery",
  "trash",
  "settings",
]);

export const parseClassTab = (search: string): ClassTab => {
  const value = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  ).get("tab");
  return CLASS_TABS.has(value ?? "") ? (value as ClassTab) : "canvases";
};

export type BetaStatusFilter = "PENDING" | "APPROVED" | "REJECTED";

/** `?status=PENDING` opens the beta screen on that tab; anything else is all. */
export const parseBetaStatus = (search: string): BetaStatusFilter | null => {
  const value = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  ).get("status");
  return value === "PENDING" || value === "APPROVED" || value === "REJECTED"
    ? value
    : null;
};

export const classPath = (classId: string, tab: ClassTab = "canvases") =>
  tab === "canvases" ? `/classes/${classId}` : `/classes/${classId}?tab=${tab}`;
/** 학생마다 아이디를 채운 안내문. `print`면 들어가자마자 인쇄 창을 연다. */
export const classHandoutPath = (classId: string, print = false) =>
  `/classes/${classId}/handout${print ? "?print=1" : ""}`;
export const canvasPath = (classId: string, canvasId: string) =>
  `/classes/${classId}/canvases/${canvasId}`;
export const revisionsPath = (classId: string, canvasId: string) =>
  `${canvasPath(classId, canvasId)}/revisions`;

export type Navigate = (
  path: string,
  options?: { replace?: boolean; force?: boolean },
) => void;

/** Returns false to keep the user on the current page. */
type LeaveGuard = (proceed: () => void) => boolean;
let leaveGuard: LeaveGuard | null = null;

/** Registers the check run before leaving the page, or clears it. */
export const setLeaveGuard = (guard: LeaveGuard | null) => {
  leaveGuard = guard;
};

export const confirmLeave = (proceed: () => void = () => undefined): boolean =>
  !leaveGuard || leaveGuard(proceed);

export const NavigationContext = createContext<Navigate>(() => undefined);

export const useNavigate = (): Navigate => useContext(NavigationContext);

/** Only same-application absolute paths are navigable; no open redirects. */
const isInternalPath = (path: string) =>
  path.startsWith("/") && !path.startsWith("//") && !path.includes("\\");

type LocationState = { pathname: string; search: string };
type ParsedHref = LocationState & { hash: string };

const readLocation = (): LocationState => ({
  pathname: window.location.pathname,
  search: window.location.search,
});

const hrefOf = ({ pathname, search }: LocationState) => pathname + search;

const parseHref = (path: string): ParsedHref => {
  const url = new URL(path, window.location.origin);
  return { pathname: url.pathname, search: url.search, hash: url.hash };
};

export const useLocationRoute = () => {
  const [location, setLocation] = useState(readLocation);
  const current = useRef(location);
  current.current = location;
  useEffect(() => {
    const onPopState = () => {
      const next = readLocation();
      if (
        next.pathname === current.current.pathname &&
        next.search === current.current.search
      ) {
        // Only the fragment changed; the page stays mounted.
        return;
      }
      if (
        !confirmLeave(() => {
          window.history.pushState({}, "", hrefOf(next));
          setLocation(next);
        })
      ) {
        // History cannot be cancelled; return to the page being edited.
        window.history.pushState({}, "", hrefOf(current.current));
        return;
      }
      setLocation(next);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);
  const navigate = useCallback<Navigate>((path, options) => {
    if (!isInternalPath(path)) {
      return;
    }
    const next = parseHref(path);
    const go = () => {
      // The fragment (e.g. /guide#lesson) is kept in the address only.
      const href = hrefOf(next) + next.hash;
      if (options?.replace) {
        window.history.replaceState({}, "", href);
      } else {
        window.history.pushState({}, "", href);
      }
      setLocation({ pathname: next.pathname, search: next.search });
    };
    if (!options?.force && !confirmLeave(go)) {
      return;
    }
    go();
  }, []);
  return {
    route: parseRoute(location.pathname),
    search: location.search,
    navigate,
  };
};
